/**
 * Página de Contatos do tenant
 *
 * @version 2.0.0
 * RELEASE: 2026-08-28 v2.0.0 - Lista e KPIs migrados para TanStack Query
 *          (chaves 'contacts-paginated' e 'contacts-stats'). Corrige o caso em
 *          que contatos criados pelo botão "+" global (criação rápida) só
 *          apareciam após F5, já que a tela usava estado local e ignorava a
 *          invalidação de cache emitida pelo provider global.
 */
import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { LucideIcon } from 'lucide-react';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Tags, 
  Tag,
  Users,
  Phone,
  Mail,
  X,
  Download,
  MessageSquare,
  Smartphone,
  CheckCircle,
  Instagram,  // Ícone para coluna Instagram
  GitMerge,   // Ícone para unificar contatos
  FileSpreadsheet, // Ícone do atalho "Importar Lista"
  UserPlus, // Ícone da ação "Transformar em cliente"
  BadgeCheck, // v1.2.0: selo de contato que já é cliente

} from 'lucide-react';
// Navegação para a Central de Importação de Listas
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { getStorageUrl } from '@/lib/storage';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';
import { CreateContactDialog } from '@/components/tenant/contacts/CreateContactDialog';
import { DealTagBadge } from '@/components/crm/deals/tags';
import { EditContactDialog } from '@/components/tenant/contacts/EditContactDialog';
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
// Conversão contato → cliente (reaproveita o formulário padrão de clientes)
import { CustomerFormDialog } from '@/components/crm/customers/CustomerFormDialog';
import { TagManagementDialog } from '@/components/tenant/contacts/TagManagementDialog';
import { AssignTagsDialog } from '@/components/tenant/contacts/AssignTagsDialog';
import { ImportContactsDialog } from '@/components/tenant/contacts/ImportContactsDialog';
import { StartConversationDialog } from '@/components/tenant/contacts/StartConversationDialog';
import { MergeContactsDialog } from '@/components/tenant/contacts/MergeContactsDialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { logger } from '@/lib/logger';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';

// ============================================================================
// INTERFACES - Definição de tipos para contatos e tags
// ============================================================================

/** Interface para tags de contato */
interface ContactTag {
  id: string;
  name: string;
  color: string;
}

/** Interface para contato com suas tags */
interface Contact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
  created_at: string;
  tags: ContactTag[];
  is_saved_to_agenda?: boolean;
  /** Metadata do contato (inclui source e dados extras) */
  metadata?: {
    username?: string;
    source?: string;
    igsid?: string;
  } | null;
  /** Channel IDs do contato (Instagram, WhatsApp, etc) - NOVO: Fonte principal do @username Instagram */
  channel_ids?: {
    instagram_username?: string;
    instagram_igsid?: string;
  } | null;
  /** v1.1.0 (RPC): vendedor vinculado ao contato */
  seller_id?: string | null;
  /** v1.2.0 (RPC): vínculo com cliente cadastrado no CRM */
  customer_id?: string | null;
  is_customer?: boolean;
  seller_name?: string | null;
  seller_avatar?: string | null;
  updated_at?: string | null;
}

/** Interface para estatísticas de contatos (KPIs) */
interface ContactStats {
  total_count: number;
  with_phone_count: number;
  with_email_count: number;
  with_tags_count: number;
  saved_to_agenda_count: number;
}

/** Valor padrão dos KPIs enquanto a query não retorna (evita undefined na UI) */
const EMPTY_CONTACT_STATS: ContactStats = {
  total_count: 0,
  with_phone_count: 0,
  with_email_count: 0,
  with_tags_count: 0,
  saved_to_agenda_count: 0,
};



// ============================================================================
// COMPONENTES AUXILIARES - Cards de métricas (padrão Clientes)
// ============================================================================

/** Props do componente MetricCard */
interface MetricCardProps {
  title: string;
  value: number;
  icon: LucideIcon;
  variant?: 'primary' | 'success' | 'info' | 'purple';
}

/** Mapeamento de variantes para cores (padrão DealListKpiCards) */
const contactVariantStyles = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  purple: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
};

/** Card de KPI compacto horizontal (padrão unificado DealListKpiCards) */
function MetricCard({ title, value, icon: Icon, variant = 'primary' }: MetricCardProps) {
  const styles = contactVariantStyles[variant];
  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL - Gerenciamento de contatos do tenant
// ============================================================================

/**
 * Componente de gerenciamento de contatos do tenant
 * Utiliza RPCs para paginação no banco e estatísticas agregadas
 * 
 * Permissões granulares:
 * - canCreate('contacts'): criar e importar contatos
 * - canEdit('contacts'): editar contatos e gerenciar tags
 * - canDelete('contacts'): excluir contatos
 * - isAdmin: gerenciar tags (admin-only)
 */
const TenantContacts = () => {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const { canView, canCreate, canEdit, canDelete, isAdmin } = usePermissions();

  // Conversão contato → cliente
  const [convertContact, setConvertContact] = useState<Contact | null>(null);
  const [convertingContactId, setConvertingContactId] = useState<string | null>(null);
  const [linkedCustomerId, setLinkedCustomerId] = useState<string | null>(null);
  const navigate = useNavigate();
  // Atalho para a Central de Importação de Listas (mesma permissão do menu)
  const canImportLists = canView('lists_contacts');
  // v1.0.0 - Permissões granulares para Tags de Contato (contact_tags)
  const canViewTags = canView('contact_tags');
  const canCreateTags = canCreate('contact_tags');
  const canEditTags = canEdit('contact_tags');
  const canDeleteTags = canDelete('contact_tags');
  
  // Tags continuam em estado local (lista pequena, carregada uma vez)
  const [tags, setTags] = useState<ContactTag[]>([]);

  
  // Filtros e busca
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedTagFilter, setSelectedTagFilter] = useState<string | null>(null);
  
  // Estados dos dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Auto-abre modal quando vindo do botão 'Novo' do header (Quick Create)
  useAutoOpenFromQuery('1', () => setCreateDialogOpen(true));
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [tagManagementOpen, setTagManagementOpen] = useState(false);
  const [assignTagsOpen, setAssignTagsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [startConversationOpen, setStartConversationOpen] = useState(false);
  // Auto-abre modal quando vindo do botão 'Novo' do header (Quick Create)
  useAutoOpenFromQuery('chat', () => setStartConversationOpen(true));
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  // Contato aberto no modal de detalhes (360º) — reaproveita o modal de cliente.
  // v2.0.0: guardamos apenas o snapshot da linha clicada; os dados exibidos são
  // sempre relidos da lista atual (query), evitando modal com dados velhos.
  const [detailsContactSnapshot, setDetailsContactSnapshot] = useState<Contact | null>(null);

  
  // Estados de paginação (o total vem junto da query paginada)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);


  // Computed: verifica se há filtros ativos
  const hasActiveFilters = searchQuery || selectedTagFilter;

  // Limpa todos os filtros
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTagFilter(null);
  };

  // ============================================================================
  // DEBOUNCE - Atraso na busca para evitar requisições excessivas
  // ============================================================================

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  /**
   * Deep link vindo da Pesquisa Global do header: ?q=<termo>
   * Pré-preenche o campo de busca da listagem e limpa o parâmetro da URL.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qParam = params.get('q');
    if (qParam) {
      setSearchQuery(qParam);
      setDebouncedSearch(qParam);
      params.delete('q');
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
    // Executa apenas na montagem da página
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // ============================================================================
  // QUERIES - Carregamento de dados via RPC (TanStack Query)
  //
  // v2.0.0: lista e KPIs migrados de estado local para TanStack Query. Isso
  // permite que qualquer criação/edição feita FORA desta tela (ex.: botão "+"
  // global de criação rápida) invalide as chaves e a tela se atualize sozinha,
  // sem depender de F5. As RPCs e o mapeamento de dados são os mesmos.
  // ============================================================================

  /** KPIs agregados no banco (chave: contacts-stats) */
  const { data: stats = EMPTY_CONTACT_STATS } = useQuery({
    queryKey: ['contacts-stats', currentTenant?.id],
    enabled: !!currentTenant?.id,
    queryFn: async (): Promise<ContactStats> => {
      const { data, error } = await supabase.rpc('get_tenant_contact_stats', {
        p_tenant_id: currentTenant!.id,
      });

      if (error) throw error;

      // RPC retorna array com 1 linha
      const row: any = data && data.length > 0 ? data[0] : null;
      if (!row) return EMPTY_CONTACT_STATS;

      return {
        total_count: Number(row.total_count) || 0,
        with_phone_count: Number(row.with_phone_count) || 0,
        with_email_count: Number(row.with_email_count) || 0,
        with_tags_count: Number(row.with_tags_count) || 0,
        saved_to_agenda_count: Number(row.saved_to_agenda_count) || 0,
      };
    },
  });

  /** Contatos paginados (chave: contacts-paginated + filtros) */
  const {
    data: contactsPage,
    isLoading: loading,
    error: contactsError,
  } = useQuery({
    queryKey: [
      'contacts-paginated',
      currentTenant?.id,
      currentPage,
      pageSize,
      debouncedSearch,
      selectedTagFilter,
    ],
    enabled: !!currentTenant?.id,
    placeholderData: (prev) => prev, // mantém a página anterior enquanto recarrega
    queryFn: async (): Promise<{ items: Contact[]; total: number }> => {
      const offset = (currentPage - 1) * pageSize;

      const { data, error } = await supabase.rpc('get_tenant_contacts_paginated', {
        p_tenant_id: currentTenant!.id,
        p_limit: pageSize,
        p_offset: offset,
        p_search: debouncedSearch || null,
        p_tag_id: selectedTagFilter || null,
      });

      if (error) throw error;

      if (!data || data.length === 0) return { items: [], total: 0 };

      // Mapeia os contatos com tags parseadas do JSONB
      const mappedContacts: Contact[] = data.map((row: any) => ({
        id: row.id,
        name: row.name,
        phone: row.phone,
        email: row.email,
        avatar_url: row.avatar_url,
        is_saved_to_agenda: row.is_saved_to_agenda,
        created_at: row.created_at,
        updated_at: row.updated_at ?? null,
        tags: Array.isArray(row.tags) ? row.tags : [],
        metadata: row.metadata || null,
        channel_ids: row.channel_ids || null,  // NOVO: Contém instagram_username
        // v1.1.0: vendedor vinculado (usado na coluna Vendedor e no modal de edição)
        seller_id: row.seller_id ?? null,
        seller_name: row.seller_name ?? null,
        seller_avatar: row.seller_avatar ?? null,
        // v1.2.0: vínculo com cliente vindo da própria RPC (sem N+1)
        customer_id: row.customer_id ?? null,
        is_customer: !!row.is_customer,
      }));

      // total_count vem em cada linha, pegamos da primeira
      return { items: mappedContacts, total: Number((data[0] as any).total_count) || 0 };
    },
  });

  const contacts = contactsPage?.items ?? [];
  const totalContacts = contactsPage?.total ?? 0;

  /**
   * Contato exibido no modal 360º.
   * Sempre prioriza a versão mais recente vinda da lista (query); o snapshot da
   * linha clicada é apenas fallback enquanto a lista recarrega.
   */
  const detailsContact = detailsContactSnapshot
    ? contacts.find((c) => c.id === detailsContactSnapshot.id) ?? detailsContactSnapshot
    : null;


  // Feedback de erro preservado (antes era um toast dentro do fetch manual)
  useEffect(() => {
    if (contactsError) {
      logger.error('[Contacts]', `Erro ao buscar contatos paginados: ${(contactsError as Error)?.message ?? contactsError}`);
      toast.error('Erro ao carregar contatos');
    }
  }, [contactsError]);

  /** Busca todas as tags do tenant via RPC server-side (sem SELECT direto) */
  const fetchTags = useCallback(async () => {
    if (!currentTenant?.id) return;

    try {
      // RPC get_tenant_contact_tags valida tenant e retorna id/name/color
      const { data, error } = await supabase.rpc('get_tenant_contact_tags', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;
      setTags(data || []);
    } catch (error) {
      logger.error('[Contacts] Erro ao buscar tags:', error);
    }
  }, [currentTenant?.id]);

  // Carrega tags uma vez no mount (KPIs já vêm por query)
  useEffect(() => {
    if (currentTenant?.id) {
      fetchTags();
    }
  }, [currentTenant?.id, fetchTags]);


  // Reseta para página 1 quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedTagFilter]);

  // ============================================================================
  // HANDLERS - Ações do usuário
  // ============================================================================

  const handleEdit = (contact: Contact) => {
    setSelectedContact(contact);
    setEditDialogOpen(true);
  };

  const handleDelete = (contact: Contact) => {
    setSelectedContact(contact);
    setDeleteDialogOpen(true);
  };

  const handleAssignTags = (contact: Contact) => {
    setSelectedContact(contact);
    setAssignTagsOpen(true);
  };

  /**
   * Handler "Transformar em cliente".
   * O vínculo contato ↔ cliente é checado sob demanda (não na listagem, para
   * não pesar a consulta). Se já existir cliente, apenas oferece abri-lo.
   */
  const handleConvertToCustomer = async (contact: Contact) => {
    // v1.2.0: quando a listagem já informa o vínculo, evita a checagem extra
    if (contact.is_customer && contact.customer_id) {
      setLinkedCustomerId(contact.customer_id);
      return;
    }

    setConvertingContactId(contact.id);
    try {
      const { data, error } = await supabase.rpc('get_contact_linked_customer', {
        p_contact_id: contact.id,
      });
      if (error) throw error;

      const linked = data && data.length > 0 ? data[0] : null;
      if (linked?.id) {
        toast.info('Este contato já é um cliente.', {
          action: {
            label: 'Abrir cliente',
            onClick: () => setLinkedCustomerId(linked.id),
          },
        });
        return;
      }

      setConvertContact(contact);
    } catch (err: any) {
      logger.error('[Contacts] Erro ao verificar cliente vinculado:', err?.message);
      toast.error('Não foi possível verificar se este contato já é um cliente.');
    } finally {
      setConvertingContactId(null);
    }
  };

  /** Handler para iniciar conversa com contato */
  const handleStartConversation = (contact: Contact) => {
    if (!contact.phone) {
      toast.error('Este contato não possui número de telefone');
      return;
    }
    setSelectedContact(contact);
    setStartConversationOpen(true);
  };

  /**
   * Atualiza dados após ação (criar, editar, deletar, importar).
   * v2.0.0: invalida as chaves do TanStack Query — as mesmas usadas pelo
   * provider global de criação rápida.
   */
  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['contacts-stats'] });
    queryClient.invalidateQueries({ queryKey: ['contacts-paginated'] });
  };


  /** Confirma exclusão de contato */
  const confirmDelete = async () => {
    if (!selectedContact) return;

    try {
      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('id', selectedContact.id);

      if (error) throw error;

      toast.success('Contato excluído com sucesso');
      handleRefresh();
    } catch (error) {
      logger.error('[Contacts] Erro ao excluir contato:', error);
      toast.error('Erro ao excluir contato');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedContact(null);
    }
  };

  // ============================================================================
  // COMPUTED - Valores calculados
  // ============================================================================

  /** Obtém iniciais do nome para o avatar */
  const getInitials = (name: string | null) => {
    if (!name) return 'C';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // ============================================================================
  // RENDER - Interface principal (padrão CrmCustomers)
  // ============================================================================

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto */}
      {/* Mobile: título + ações na mesma linha (subtítulo oculto). Desktop: layout original. */}
      <div className="flex flex-col gap-3 p-4 border-b sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center justify-between gap-2 sm:block">
          <div>
            <h1 className="text-xl font-semibold">Contatos</h1>
            <p className="hidden sm:block text-xs text-muted-foreground">
              Gerencie seus contatos e segmente com tags
            </p>
          </div>
          {/* Ações mobile — alinhadas à direita na mesma linha do título */}
          <div className="flex flex-wrap items-center gap-2 sm:hidden">
            {canViewTags && (
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => setTagManagementOpen(true)}
                title="Gerenciar Tags"
              >
                <Tags className="h-3.5 w-3.5" />
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => setMergeDialogOpen(true)}
                title="Unificar Contatos"
              >
                <GitMerge className="h-3.5 w-3.5" />
              </Button>
            )}
            {/* Atalho mobile para importação de listas */}
            {canImportLists && (
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => navigate('/tenant/lists?tab=contacts')}
                title="Importar Lista"
              >
                <FileSpreadsheet className="h-3.5 w-3.5" />
              </Button>
            )}
            {canCreate('contacts') && (
              <Button
                size="xs"
                onClick={() => setCreateDialogOpen(true)}
                className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
              >
                <Plus className="sm:mr-1.5" />
                <span className="hidden sm:inline">Novo Contato</span>
              </Button>
            )}
          </div>
        </div>
        {/* Ações desktop */}
        <div className="hidden sm:flex flex-wrap gap-2">
          {canViewTags && (
            <Button
              variant="outline"
              size="xs"
              onClick={() => setTagManagementOpen(true)}
              className="hover:shadow-md transition-all duration-200"
            >
              <Tags className="mr-1.5" />
              Gerenciar Tags
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="xs"
              onClick={() => setMergeDialogOpen(true)}
              className="hover:shadow-md transition-all duration-200"
            >
              <GitMerge className="mr-1.5" />
              Unificar Contatos
            </Button>
          )}
          {/* Atalho desktop para a Central de Importação de Listas */}
          {canImportLists && (
            <Button
              variant="outline"
              size="xs"
              onClick={() => navigate('/tenant/lists?tab=contacts')}
              className="hover:shadow-md transition-all duration-200"
            >
              <FileSpreadsheet className="mr-1.5" />
              Importar Lista
            </Button>
          )}
          {canCreate('contacts') && (
            <Button
              size="xs"
              onClick={() => setCreateDialogOpen(true)}
              className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
            >
              <Plus className="mr-1.5" />
              Novo Contato
            </Button>
          )}
        </div>
      </div>


      {/* KPI Cards - Métricas agregadas (padrão compacto) */}
      {!loading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
          <MetricCard
            title="Total Contatos"
            value={stats.total_count}
            icon={Users}
            variant="primary"
          />
          <MetricCard
            title="Com Telefone"
            value={stats.with_phone_count}
            icon={Phone}
            variant="success"
          />
          <MetricCard
            title="Com Tags"
            value={stats.with_tags_count}
            icon={Tags}
            variant="info"
          />
          <MetricCard
            title="Na Agenda"
            value={stats.saved_to_agenda_count}
            icon={Smartphone}
            variant="purple"
          />
        </div>
      )}

      {/* Barra de Filtros Inline (padrão CrmCustomers) */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        {/* Campo de busca */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, telefone ou email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        
        {/* Filtro de tag - Select padronizado */}
        <Select 
          value={selectedTagFilter || 'all'} 
          onValueChange={(v) => setSelectedTagFilter(v === 'all' ? null : v)}
        >
          <SelectTrigger className="w-[160px] h-8 text-sm">
            <SelectValue placeholder="Filtrar por tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as tags</SelectItem>
            {tags.map(tag => (
              <SelectItem key={tag.id} value={tag.id}>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: tag.color }} 
                  />
                  {tag.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Botão limpar filtros */}
        {hasActiveFilters && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={clearFilters}
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3 mr-1" />
            Limpar
          </Button>
        )}

        {/* Indicador de carregamento */}
        {loading && (
          <span className="text-xs text-muted-foreground animate-pulse">
            Atualizando...
          </span>
        )}
      </div>

      {/* Container da Tabela (sem Card wrapper) */}
      <div className="flex-1 overflow-auto">
        {loading && contacts.length === 0 ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : contacts.length === 0 ? (
          /* Estado vazio compacto */
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Users className="h-12 w-12 text-muted-foreground/40" />
            <div className="text-center">
              <h3 className="font-medium text-sm">
                {stats.total_count === 0 ? 'Nenhum contato cadastrado' : 'Nenhum contato encontrado'}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.total_count === 0 ? 'Cadastre seu primeiro contato' : 'Ajuste os filtros de busca'}
              </p>
            </div>
            {stats.total_count === 0 && canCreate('contacts') && (
              <Button size="xs" onClick={() => setCreateDialogOpen(true)}>
                <Plus className="mr-1.5" />
                Novo Contato
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs font-medium">Contato</TableHead>
                <TableHead className="text-xs font-medium">Telefone</TableHead>
                <TableHead className="text-xs font-medium">Email</TableHead>
                <TableHead className="text-xs font-medium">Instagram</TableHead>
                <TableHead className="text-xs font-medium">Tags</TableHead>
                {/* Coluna Vendedor vinculado ao contato */}
                <TableHead className="text-xs font-medium">Vendedor</TableHead>
                <TableHead className="text-xs font-medium">Criado em</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow 
                  key={contact.id} 
                  className="hover:bg-muted/40 group cursor-pointer"
                  onClick={() => setDetailsContactSnapshot(contact)}
                >

                  {/* Coluna: Contato (Avatar + Nome) */}
                  <TableCell className="py-2">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={getStorageUrl(contact.avatar_url)} />
                        <AvatarFallback className={cn("font-medium text-xs", getAvatarColorClasses(contact.name))}>
                          {getInitials(contact.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-sm text-foreground">
                        {contact.name || 'Sem nome'}
                      </span>
                      {/* v1.2.0: selo verde quando o contato já possui cliente cadastrado */}
                      {contact.is_customer && (
                        <Badge
                          /* v1.4.0: pill compacto e discreto (verde suave, sem borda) —
                             mantém a identificação sem competir com o nome do contato */
                          className="gap-1 h-5 px-1.5 text-[10px] font-medium bg-success/10 text-success hover:bg-success/15 cursor-pointer border-transparent"

                          onClick={(e) => {
                            e.stopPropagation();
                            if (contact.customer_id) setLinkedCustomerId(contact.customer_id);
                          }}
                        >
                          <BadgeCheck className="h-3 w-3" />
                          Cliente
                        </Badge>
                      )}
                    </div>
                  </TableCell>

                  {/* Coluna: Telefone */}
                  <TableCell className="py-2">
                    {contact.phone ? (
                      <div className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{contact.phone}</span>
                        {/* Ícone indicando salvo na agenda (sem badge) */}
                        {contact.is_saved_to_agenda && (
                          <span title="Salvo na agenda" className="inline-flex">
                            <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground/50 text-sm">—</span>
                    )}
                  </TableCell>

                  {/* Coluna: Email */}
                  <TableCell className="py-2">
                    {contact.email ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-3.5 w-3.5" />
                        <span className="truncate max-w-[200px] text-sm">{contact.email}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground/50 text-sm">—</span>
                    )}
                  </TableCell>

                  {/* Coluna: Instagram - Prioriza channel_ids (fonte principal), fallback para metadata */}
                  <TableCell className="py-2">
                    {(() => {
                      // Prioridade 1: channel_ids (onde Instagram armazena o username)
                      // Prioridade 2: metadata.username (legado/fallback)
                      const instagramUsername = contact.channel_ids?.instagram_username || 
                                                 contact.metadata?.username;
                      
                      return instagramUsername ? (
                        <div className="flex items-center gap-2">
                          <Instagram className="h-3.5 w-3.5 text-pink-500" />
                          <span className="text-sm text-muted-foreground font-medium">
                            @{instagramUsername}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground/50 text-sm">—</span>
                      );
                    })()}
                  </TableCell>

                  {/* Coluna: Tags - mesmo formato do KanbanCard (ícone + nome colorido) */}
                  <TableCell className="py-2">
                    {contact.tags.length > 0 ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {contact.tags.slice(0, 3).map(tag => (
                          <div
                            key={tag.id}
                            className="flex items-center gap-0.5 text-[10px] font-medium"
                            style={{ color: tag.color }}
                          >
                            <Tag size={10} />
                            <span className="max-w-[80px] truncate">{tag.name}</span>
                          </div>
                        ))}
                        {contact.tags.length > 3 && (
                          <Badge
                            variant="secondary"
                            className="text-[10px] px-1 py-0 h-4"
                            title={contact.tags.slice(3).map(t => t.name).join(', ')}
                          >
                            +{contact.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground/50 text-sm">—</span>
                    )}
                  </TableCell>

                  {/* Coluna: Vendedor vinculado ao contato */}
                  <TableCell className="py-2">
                    {contact.seller_name ? (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={getStorageUrl(contact.seller_avatar)} />
                          <AvatarFallback className={cn("font-medium text-[10px]", getAvatarColorClasses(contact.seller_name))}>
                            {contact.seller_name.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm truncate max-w-[140px]">{contact.seller_name}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground/50 text-sm">—</span>
                    )}
                  </TableCell>

                  {/* Coluna: Data de criação */}
                  <TableCell className="py-2 text-sm text-muted-foreground">
                    {format(new Date(contact.created_at), "dd/MM/yyyy", { locale: ptBR })}
                  </TableCell>


                  {/* Coluna: Ações (visível no hover) — não abre o modal de detalhes */}
                  <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {/* Editar - permissão de edição */}
                        {canEdit('contacts') && (
                          <DropdownMenuItem onClick={() => handleEdit(contact)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                        )}
                        {/* Gerenciar Tags - permissão de edição */}
                        {canEdit('contacts') && (
                          <DropdownMenuItem onClick={() => handleAssignTags(contact)}>
                            <Tags className="h-4 w-4 mr-2" />
                            Gerenciar Tags
                          </DropdownMenuItem>
                        )}
                        {/* Iniciar Conversa - sempre disponível para quem pode ver */}
                        <DropdownMenuItem onClick={() => handleStartConversation(contact)}>
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Iniciar Conversa
                        </DropdownMenuItem>
                        {/* Transformar em cliente - exige permissão de criação em Clientes */}
                        {canCreate('crm_customers') && (
                          <DropdownMenuItem
                            disabled={convertingContactId === contact.id || !!contact.is_customer}
                            onSelect={() => {
                              // v1.1.0: não usar preventDefault para que o popover feche
                              // antes de abrir o modal de conversão (evita menu "preso" na tela)
                              handleConvertToCustomer(contact);
                            }}
                          >
                            <UserPlus className="h-4 w-4 mr-2" />
                            {contact.is_customer ? 'Já é cliente' : 'Transformar em cliente'}
                          </DropdownMenuItem>
                        )}
                        {/* Excluir - permissão de exclusão */}
                        {canDelete('contacts') && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDelete(contact)}
                              className="text-destructive focus:text-destructive focus:bg-destructive/10"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        
        {/* Footer de paginação */}
        {totalContacts > 0 && (
          <div className="px-4 pb-4">
            <PaginationFooter
              totalItems={totalContacts}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 25, 50, 100]}
              borderClassName="border-t border-border/50"
            />
          </div>
        )}
      </div>

      {/* ====================================================================== */}
      {/* DIALOGS - Modais de criação, edição e gerenciamento                    */}
      {/* ====================================================================== */}

      <CreateContactDialog
        open={createDialogOpen && !!currentTenant}
        onOpenChange={setCreateDialogOpen}
        tenantId={currentTenant?.id ?? ''}
        onSuccess={handleRefresh}
      />

      <EditContactDialog
        open={editDialogOpen && !!selectedContact}
        onOpenChange={setEditDialogOpen}
        contact={selectedContact}
        onSuccess={handleRefresh}
      />

      {/* Modal 360º do contato — abre o cliente vinculado quando existir */}
      <CustomerDetailsModalPremium
        customerId={null}
        contactId={detailsContact?.id ?? null}
        contactPreview={detailsContact ? {
          id: detailsContact.id,
          name: detailsContact.name,
          phone: detailsContact.phone,
          email: detailsContact.email,
          avatar_url: detailsContact.avatar_url,
          created_at: (detailsContact as any).created_at ?? null,
          updated_at: (detailsContact as any).updated_at ?? null,
          // v1.1.0: vendedor vinculado, exibido na sidebar do modal
          seller_id: detailsContact.seller_id ?? null,
          seller_name: detailsContact.seller_name ?? null,
          seller_avatar: detailsContact.seller_avatar ?? null,
        } : null}
        open={!!detailsContact}
        onOpenChange={(o) => { if (!o) setDetailsContactSnapshot(null); }}
      />

      {/* Conversão contato → cliente (validações de duplicidade no backend) */}
      {convertContact && (
        <CustomerFormDialog
          customer={null}
          open={!!convertContact}
          onOpenChange={(o) => { if (!o) setConvertContact(null); }}
          prefillContact={{
            id: convertContact.id,
            name: convertContact.name,
            phone: convertContact.phone,
            email: convertContact.email,
            // Herda o vendedor responsável do contato para o novo cliente
            seller_id: (convertContact as any).seller_id ?? null,
          }}
          onSuccess={(newCustomer) => {
            setConvertContact(null);
            handleRefresh();
            if (newCustomer?.id) {
              toast.success('Contato transformado em cliente!', {
                action: { label: 'Abrir cliente', onClick: () => setLinkedCustomerId(newCustomer.id) },
              });
            }
          }}
          onSelectExistingCustomer={(existing) => {
            setConvertContact(null);
            if (existing?.id) setLinkedCustomerId(existing.id);
          }}
        />
      )}

      {/* Abre o cliente vinculado/criado a partir do contato */}
      <CustomerDetailsModalPremium
        customerId={linkedCustomerId}
        open={!!linkedCustomerId}
        onOpenChange={(o) => { if (!o) setLinkedCustomerId(null); }}
      />



      <TagManagementDialog
        open={tagManagementOpen && !!currentTenant}
        onOpenChange={setTagManagementOpen}
        tenantId={currentTenant?.id ?? ''}
        canCreate={canCreateTags}
        canEdit={canEditTags}
        canDelete={canDeleteTags}
        onSuccess={() => {
          fetchTags();
          handleRefresh();
        }}

      />

      <AssignTagsDialog
        open={assignTagsOpen && !!selectedContact}
        onOpenChange={setAssignTagsOpen}
        contact={selectedContact}
        availableTags={tags}
        tenantId={currentTenant?.id ?? ''}
        onSuccess={handleRefresh}
      />

      <ImportContactsDialog
        open={importDialogOpen && !!currentTenant}
        onOpenChange={setImportDialogOpen}
        tenantId={currentTenant?.id ?? ''}
        existingPhones={[]}
        onSuccess={handleRefresh}
      />

      <StartConversationDialog
        open={startConversationOpen && !!selectedContact}
        onOpenChange={setStartConversationOpen}
        contact={selectedContact}
        tenantId={currentTenant?.id ?? ''}
      />

      {/* Dialog de unificação de contatos (merge) */}
      <MergeContactsDialog
        open={mergeDialogOpen}
        onOpenChange={setMergeDialogOpen}
        tenantId={currentTenant?.id ?? ''}
        onSuccess={handleRefresh}
      />
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir contato?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa ação não pode ser desfeita. Isso irá excluir permanentemente o 
              contato "{selectedContact?.name || selectedContact?.phone}" e todo o seu histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TenantContacts;
